import { normalizeSalesStatus, patchCampaignListData, patchDetailData, toWireSalesStatus } from './isrLeadCachePatch';
import type { ISRUpdateSnapshot } from '../types';

/**
 * Registry of ISR saves whose write-back has NOT yet been observed in the
 * leads source. n8n accepts update_campaign immediately, but the leads
 * records it feeds back through get_lead / get_leads_cards can lag by
 * MINUTES. Every refetch inside that window would read the OLD values and
 * silently bounce the user's saved edits off the screen.
 *
 * Lifecycle:
 *   1. useISRLeadMutation.onSuccess records the snapshot with a deadline
 *      (PENDING_TTL_MS — generous, bounded, never grows forever).
 *   2. readPendingSnapshot() is consulted by the detail fetcher right after
 *      the server responds: fields the server still reports stale get
 *      overwritten with the saved values before the cache is written, so
 *      the workspace shows what the user actually saved.
 *   3. When the server OBSERVES the saved value (or the deadline lapses —
 *      the server is then presumed authoritative), the entry is dropped.
 *
 * Status saves already have getPreferredStatus guarding this exact race;
 * this extends the same protection to contact fields, notes, and substatus.
 */

const PENDING_TTL_MS = 10 * 60 * 1000; // 10 min — n8n write-back observed well inside this in practice

interface PendingWrite {
  campaignId: string;
  lvl2Id: string;
  snapshot: ISRUpdateSnapshot;
  /** When this entry stops defending the saved values. */
  expiresAt: number;
}

const pendingWrites = new Map<string, PendingWrite>();

const entryKey = (campaignId: string, lvl2Id: string | number): string =>
  `${campaignId}::${String(lvl2Id)}`;

/** Record a successful save so later refetches can defend its values. */
export const rememberIsrSave = (
  campaignId: string,
  lvl2Id: string | number,
  snapshot: ISRUpdateSnapshot,
): void => {
  if (!campaignId.trim() || !String(lvl2Id).trim()) return;
  pendingWrites.set(entryKey(campaignId, lvl2Id), {
    campaignId,
    lvl2Id: String(lvl2Id),
    snapshot,
    expiresAt: Date.now() + PENDING_TTL_MS,
  });

  // Opportunistic GC alongside every record — the registry holds at most one
  // entry per lead the user has edited this session.
  if (pendingWrites.size > 64) {
    const now = Date.now();
    for (const [key, entry] of pendingWrites) {
      if (entry.expiresAt <= now) pendingWrites.delete(key);
    }
  }
};

/**
 * After the server confirms the saved value on a field, the server becomes
 * authoritative again — stop defending it. Called for the whole lead once
 * the detail response matches the snapshot.
 */
export const forgetIsrSave = (campaignId: string, lvl2Id: string | number): void => {
  pendingWrites.delete(entryKey(campaignId, lvl2Id));
};

const text = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const cleaned = String(value).trim();
  return cleaned && cleaned.toLowerCase() !== 'unknown' ? cleaned : null;
};

/**
 * True when the server has caught up with this snapshot — every saved field
 * it carries now matches. Compares the same string-coercion the workspace
 * uses so format drift (null vs '') never keeps the entry alive forever.
 */
const serverObservedSnapshot = (fullLead: Record<string, any>, snapshot: ISRUpdateSnapshot): boolean => {
  const lvl2 = (fullLead.personLvl2 ?? fullLead.rawOriginalData?.lvl2 ?? {}) as Record<string, any>;
  const lvl1Person = (fullLead.personLvl1 ?? fullLead.rawOriginalData?.lvl1_person ?? {}) as Record<string, any>;
  const lvl1Company = (fullLead.companyLvl1 ?? fullLead.rawOriginalData?.lvl1_company ?? {}) as Record<string, any>;

  const personSources = [lvl2, lvl1Person];
  const personField = (key: string): string | null => {
    for (const source of personSources) {
      const value = text(source[key]);
      if (value !== null) return value;
    }
    return null;
  };

  for (const key of ['full_name', 'role_position', 'email_id', 'phone_id', 'mobile_id', 'whatsapp_id', 'linkedin_id', 'twitter_x_id', 'instagram_id', 'facebook_id'] as const) {
    const saved = text((snapshot.person as unknown as Record<string, unknown>)[key]);
    // full_name has no direct lvl2 key — n8n writes it to `name`.
    const observed = personField(key === 'full_name' ? 'name' : key);
    if (saved !== null && observed !== saved) return false;
  }

  const companyField = (key: string): string | null =>
    text(lvl1Company[key] ?? lvl2[`company_${key}`] ?? lvl2[key]);

  for (const key of ['email_id', 'phone_id', 'mobile_id', 'whatsapp_id', 'website_id', 'linkedin_id', 'twitter_x_id', 'instagram_id', 'facebook_id', 'nr_employees', 'cnae_code', 'country', 'state', 'city', 'full_address'] as const) {
    const saved = text((snapshot.company as unknown as Record<string, unknown>)[key]);
    const observed = companyField(key);
    if (saved !== null && observed !== saved) return false;
  }

  // Status/substatus: a sales save's sync window can outlast the identity
  // fields'. Without this check, a STATUS-ONLY save's entry is discarded on
  // the first refetch (person/company already match), stripping the defense
  // and letting the lagging read bounce the user's stage change back — the
  // exact regression this registry exists to prevent.
  if (snapshot.lead_status !== null) {
    const savedStatus = normalizeSalesStatus(
      toWireSalesStatus(snapshot.lead_status, snapshot.lead_substatus),
    );
    const observedStatus = normalizeSalesStatus(lvl2.status ?? fullLead.status);
    if (savedStatus !== null && observedStatus !== null && observedStatus !== savedStatus) {
      return false;
    }

    const savedSubstatus = text(snapshot.lead_substatus);
    const observedSubstatus = text(lvl2.lead_substatus ?? fullLead.salesSubstatus);
    if (savedSubstatus !== null && observedSubstatus !== null && observedSubstatus !== savedSubstatus) {
      return false;
    }
  }

  return true;
};

/**
 * Merge the still-pending saved values over a freshly fetched full lead
 * (get_lead response, already transformed to MappedLeadData). Fields the
 * server already reports correctly are left untouched; fields still lagging
 * are overwritten with what the user saved, so the workspace NEVER shows the
 * pre-save values inside the sync window.
 *
 * Returns the lead unchanged when nothing is pending for it.
 */
export const applyPendingIsrWrites = <T extends Record<string, any>>(
  fullLead: T,
  campaignId: string,
  lvl2Id: string | number,
): T => {
  const key = entryKey(campaignId, lvl2Id);
  const entry = pendingWrites.get(key);
  if (!entry) return fullLead;

  // Deadline passed: the server had ample time — trust it from now on.
  if (entry.expiresAt <= Date.now()) {
    pendingWrites.delete(key);
    return fullLead;
  }

  if (serverObservedSnapshot(fullLead, entry.snapshot)) {
    // Server caught up — hand authority back.
    pendingWrites.delete(key);
    return fullLead;
  }

  const updatedAt = new Date().toISOString();
  const patched = patchDetailData(fullLead, null, lvl2Id, entry.snapshot, updatedAt);
  return (patched ?? fullLead) as T;
};

/** Flat card field → snapshot field (person unless prefixed company*). */
const CARD_PERSON_FIELDS: Record<string, string> = {
  leadName: 'full_name',
  leadRole: 'role_position',
  leadCity: 'city',
  leadState: 'state',
  leadCountry: 'country',
  email: 'email_id',
  phone: 'phone_id',
  mobile: 'mobile_id',
  leadWhatsapp: 'whatsapp_id',
  leadLinkedin: 'linkedin_id',
  leadTwitter: 'twitter_x_id',
  leadInstagram: 'instagram_id',
  leadFacebook: 'facebook_id',
};
const CARD_COMPANY_FIELDS: Record<string, string> = {
  companyEmail: 'email_id',
  companyPhone: 'phone_id',
  companyWebsite: 'website_id',
  companyLinkedin: 'linkedin_id',
  companyTwitter: 'twitter_x_id',
  companyInstagram: 'instagram_id',
  companyFacebook: 'facebook_id',
};

/**
 * Merge pending values over one list row (get_leads_cards response row,
 * already transformed to MinimalLeadData). Same defense as
 * applyPendingIsrWrites but for the flat card shape.
 */
export const applyPendingIsrWritesToCard = <T extends Record<string, any>>(
  card: T,
  campaignId: string,
): T => {
  const targetId = card?.id ?? card?.lead_id ?? card?.lvl2_id;
  if (targetId === undefined || targetId === null) return card;
  const key = entryKey(campaignId, targetId);
  const entry = pendingWrites.get(key);
  if (!entry) return card;

  if (entry.expiresAt <= Date.now()) {
    pendingWrites.delete(key);
    return card;
  }

  // Cards don't carry the full lvl1/lvl2 records, so the observed-check is a
  // cheap subset: if the flat card fields match the snapshot, server caught up.
  const snapshotRecord = entry.snapshot as unknown as Record<string, any>;
  const observedAll = Object.entries(CARD_PERSON_FIELDS).every(([cardField, snapshotField]) => {
    const saved = text(snapshotRecord.person[snapshotField]);
    return saved === null || text(card[cardField]) === saved;
  }) && Object.entries(CARD_COMPANY_FIELDS).every(([cardField, snapshotField]) => {
    const saved = text(snapshotRecord.company[snapshotField]);
    return saved === null || text(card[cardField]) === saved;
  });

  if (observedAll) {
    pendingWrites.delete(key);
    return card;
  }

  const updatedAt = new Date().toISOString();
  const patched = patchCampaignListData(card, targetId, entry.snapshot, updatedAt);
  return (patched ?? card) as T;
};

/** Test/teardown hook — wipes all pending state. */
export const clearPendingIsrWrites = (): void => {
  pendingWrites.clear();
};
